'use client';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { isDictationKey, measureTextareaHeight } from '@/hooks/use-auto-grow-textarea';
import { cn } from '@/lib/utils';
import type { ChatStatus } from 'ai';
import { Loader2Icon, SendIcon, SquareIcon, XIcon } from 'lucide-react';
import React, {
  Children,
  ComponentProps,
  HTMLAttributes,
  KeyboardEventHandler,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

export type PromptInputProps = HTMLAttributes<HTMLFormElement>;

export const PromptInput = ({ className, ...props }: PromptInputProps) => (
  <form
    className={cn(
      'w-full divide-y overflow-hidden rounded-xl border bg-background shadow-sm',
      className
    )}
    {...props}
  />
);

export type PromptInputTextareaProps = ComponentProps<typeof Textarea> & {
  minHeight?: number;
  maxHeight?: number;
  /** When true (default), height follows wrapped lines up to maxHeight; width stays constrained (no horizontal grow). */
  autoResizeHeight?: boolean;
};

export const PromptInputTextarea = React.forwardRef<HTMLTextAreaElement, PromptInputTextareaProps & { onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void }>(({
  onChange,
  className,
  placeholder = 'What would you like to know?',
  minHeight = 48,
  maxHeight = 164,
  autoResizeHeight = true,
  value,
  defaultValue,
  style,
  onKeyDown: onKeyDownProp,
  onCompositionStart: onCompositionStartProp,
  onCompositionEnd: onCompositionEndProp,
  ...props
}, ref) => {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const composingRef = useRef(false);
  const [height, setHeight] = React.useState(minHeight);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    },
    [ref]
  );

  const syncHeight = useCallback(() => {
    if (!autoResizeHeight || composingRef.current) return;
    const el = innerRef.current;
    if (!el) return;

    // Measure a copy. Setting the live field to height:auto drops Mac Whisper's
    // insertion point, so dictated text never lands.
    const measured = measureTextareaHeight(el);
    const next = Math.min(Math.max(measured, minHeight), maxHeight);
    setHeight((current) => (current === next ? current : next));
  }, [autoResizeHeight, minHeight, maxHeight]);

  useLayoutEffect(() => {
    syncHeight();
  }, [syncHeight, value]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    onKeyDownProp?.(e);
    if (e.defaultPrevented) return;
    // Dictation commits with Enter / keyCode 229. Sending here swallows the text.
    if (isDictationKey(e) || e.key !== 'Enter' || e.shiftKey) return;

    e.preventDefault();
    const form = e.currentTarget.form;
    if (form) {
      form.requestSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    if (autoResizeHeight) {
      requestAnimationFrame(() => syncHeight());
    }
  };

  return (
    <Textarea
      ref={setRefs}
      className={cn(
        'box-border w-full min-w-0 max-w-full resize-none rounded-none border-none p-3 shadow-none outline-none ring-0',
        autoResizeHeight
          ? 'field-sizing-fixed !min-h-0 bg-transparent dark:bg-transparent'
          : 'field-sizing-content bg-transparent dark:bg-transparent',
        'break-words [overflow-wrap:anywhere] focus-visible:ring-0 overflow-x-hidden',
        autoResizeHeight ? 'overflow-y-hidden' : 'overflow-y-auto',
        className
      )}
      {...props}
      rows={autoResizeHeight ? 1 : undefined}
      name="message"
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onCompositionStart={(event) => {
        composingRef.current = true;
        onCompositionStartProp?.(event);
      }}
      onCompositionEnd={(event) => {
        composingRef.current = false;
        onCompositionEndProp?.(event);
        syncHeight();
      }}
      placeholder={placeholder}
      style={{
        height: autoResizeHeight ? height : undefined,
        minHeight: autoResizeHeight ? minHeight : undefined,
        maxHeight,
        overflowY: autoResizeHeight && height >= maxHeight ? 'auto' : undefined,
        ...style,
      }}
    />
  );
});
PromptInputTextarea.displayName = "PromptInputTextarea";

export type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputToolbar = ({
  className,
  ...props
}: PromptInputToolbarProps) => (
  <div
    className={cn('flex items-center justify-between p-1', className)}
    {...props}
  />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div
    className={cn(
      'flex items-center gap-1',
      '[&_button:first-child]:rounded-bl-xl',
      className
    )}
    {...props}
  />
);

export type PromptInputButtonProps = ComponentProps<typeof Button>;

export const PromptInputButton = ({
  variant = 'ghost',
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const newSize =
    (size ?? Children.count(props.children) > 1) ? 'default' : 'icon';

  return (
    <Button
      className={cn(
        'shrink-0 gap-1.5 rounded-lg',
        variant === 'ghost' && 'text-muted-foreground',
        newSize === 'default' && 'px-3',
        className
      )}
      size={newSize}
      type="button"
      variant={variant}
      {...props}
    />
  );
};

export type PromptInputSubmitProps = ComponentProps<typeof Button> & {
  status?: ChatStatus;
};

export const PromptInputSubmit = ({
  className,
  variant = 'default',
  size = 'icon',
  status,
  children,
  ...props
}: PromptInputSubmitProps) => {
  let Icon = <SendIcon className="size-4" />;

  if (status === 'submitted') {
    Icon = <Loader2Icon className="size-4 animate-spin" />;
  } else if (status === 'streaming') {
    Icon = <SquareIcon className="size-4" />;
  } else if (status === 'error') {
    Icon = <XIcon className="size-4" />;
  }

  return (
    <Button
      className={cn('gap-1.5 rounded-lg', className)}
      size={size}
      type="submit"
      variant={variant}
      {...props}
    >
      {children ?? Icon}
    </Button>
  );
};

export type PromptInputModelSelectProps = ComponentProps<typeof Select>;

export const PromptInputModelSelect = (props: PromptInputModelSelectProps) => (
  <Select {...props} />
);

export type PromptInputModelSelectTriggerProps = ComponentProps<
  typeof SelectTrigger
>;

export const PromptInputModelSelectTrigger = ({
  className,
  ...props
}: PromptInputModelSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      'border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors',
      'hover:bg-accent hover:text-foreground [&[aria-expanded="true"]]:bg-accent [&[aria-expanded="true"]]:text-foreground',
      className
    )}
    {...props}
  />
);

export type PromptInputModelSelectContentProps = ComponentProps<
  typeof SelectContent
>;

export const PromptInputModelSelectContent = ({
  className,
  ...props
}: PromptInputModelSelectContentProps) => (
  <SelectContent className={cn(className)} {...props} />
);

export type PromptInputModelSelectItemProps = ComponentProps<typeof SelectItem>;

export const PromptInputModelSelectItem = ({
  className,
  ...props
}: PromptInputModelSelectItemProps) => (
  <SelectItem className={cn(className)} {...props} />
);

export type PromptInputModelSelectValueProps = ComponentProps<
  typeof SelectValue
>;

export const PromptInputModelSelectValue = ({
  className,
  ...props
}: PromptInputModelSelectValueProps) => (
  <SelectValue className={cn(className)} {...props} />
);
